import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Trap focus within `containerRef.current` while `active` is true.
 * - Tab cycles within the container
 * - Shift+Tab also cycles within
 * - On activation, focus moves to first focusable element (or initialFocus if provided)
 * - On deactivation, focus is returned to the element that was active before
 */
export function useFocusTrap<T extends HTMLElement>(
  active: boolean,
  options: { initialFocusSelector?: string } = {},
) {
  const containerRef = useRef<T | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    previouslyFocusedRef.current = (document.activeElement as HTMLElement) ?? null;

    const node = containerRef.current;
    if (!node) return;

    const moveInitialFocus = () => {
      const initial = options.initialFocusSelector
        ? (node.querySelector(options.initialFocusSelector) as HTMLElement | null)
        : (node.querySelector(FOCUSABLE_SELECTOR) as HTMLElement | null);
      initial?.focus();
    };
    // Wait one frame so layout is ready.
    const raf = requestAnimationFrame(moveInitialFocus);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = Array.from(
        node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const activeEl = document.activeElement as HTMLElement | null;

      if (e.shiftKey && activeEl === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };
    node.addEventListener('keydown', onKeyDown);

    return () => {
      cancelAnimationFrame(raf);
      node.removeEventListener('keydown', onKeyDown);
      previouslyFocusedRef.current?.focus();
    };
  }, [active, options.initialFocusSelector]);

  return containerRef;
}
